import { withIcon } from '../../../core/components/Icon';
import {
    faCheckCircle,
    faCrosshairs,
    faEllipsisV,
    faExclamationTriangle,
    faGripDotsVertical,
    faList,
    faPlus,
    faSpinnerThird,
    faTableCells,
    faLayerGroupPlus,
    faTrash,
    faLayerGroup,
    faArrowTurnDownRight,
    faBoxesStacked,
} from '@fortawesome/pro-regular-svg-icons';
import Tooltip from '../../../core/components/Tooltip.tsx';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useAssistanceContext } from './AssistanceContext';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { camelCase, snakeCase } from 'lodash';
import { AssistanceField, MemoAssistanceField } from './AssistanceField';
import AssistanceSection, { useAssistanceSectionContext } from './AssistanceSection';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu';
import classnames from '../../../core/utils/classnames.tsx';
import { useLocalStorage } from '../../../core/utils/hooks/useLocalStorage';
import { Pane, SplitPanes } from '../../../core/components/SplitPanes.tsx';
import { useAssistanceViewContext } from './AssistanceViewContext';
import { interactionStore, useInteractionSelector } from './interactionStore';
import { filterFastTrack } from './utils';
import { DEFAULT_VALUE_KEY, FormField, GeneralConfig, getItemDisplayIndex, GROUP_SEPARATOR } from './useDocumentConfig';
import AssistanceSectionEmptyState from './AssistanceSectionEmptyState';
import ItemDataFieldGroup from './customizable/ItemDataFieldGroup';
import { useConditionalComponent } from '../../../customizations/ConditionalComponentContext';
import useReselectEventListener, { ReselectEvent } from './useReselectEventListener';
import { titleCase } from '../../../core/utils/string.ts';
import { MappingDropdownMenu, useMapping, useUpdateMapping } from './MappingExplanation.tsx';
import { TABS_OPTIONS, useCurrentTabName } from './tabs.ts';
import { createTestId, ElementType, UseCase } from '../../../testing/createTestId.ts';
import VirtualizedItemList from './VirtualizedItemList.tsx';
import FineTuningPopover from './FineTuningPopover.tsx';
import { DEFAULT_LINE_ITEM_TYPE } from '../../constants.ts';
import LineItemMatchIndicator, { InlineMatchedValue, MatchedFieldsRow } from './LineItemMatchIndicator';
import FocusButton from './FocusButton';

export const TABLE_HEADER_SPLIT_PANEL_ID = 'assistance-items-table-header'; // used for column width localStorage

const ADD_LINE_ITEM_INDEX = -1;
const DRAGGABLE_ARTICLE_SET_TYPE = 'ARTICLE_SET';
const VIRTUALIZATION_THRESHOLD = 30;

// Position header fields otherwise fall back to the browser's intrinsic input width (~180px),
// which crowds out the position number and controls and forces horizontal scrolling. Constrain them.
const POSITION_FIELD_WIDTHS: Record<string, string> = {
    sort_index: 'w-24',
    position_source: 'w-40',
};
const DEFAULT_POSITION_FIELD_WIDTH = 'w-32';

const MoreIcon = withIcon(faEllipsisV);
const LoadingIcon = withIcon(faSpinnerThird);
const WarningIcon = withIcon(faExclamationTriangle);
const FocusIcon = withIcon(faCrosshairs);
const CompleteIcon = withIcon(faCheckCircle);
const AddItemIcon = withIcon(faPlus);
const ListViewIcon = withIcon(faList);
const TableViewIcon = withIcon(faTableCells);
const DragIcon = withIcon(faGripDotsVertical);
const AddAlternativeIcon = withIcon(faLayerGroupPlus);
const AlternativeIcon = withIcon(faLayerGroup);
const RemoveIcon = withIcon(faTrash);
const SubLineItemIcon = withIcon(faArrowTurnDownRight);
const RequestStockIcon = withIcon(faBoxesStacked);

interface ItemDataSectionProps {
    itemType: string;
    formFieldGroups: FormField[][];
    additionalScrollOffset?: number;
    // list-of-services specific customizations
    customTitle?: {
        positionNumber?: string;
        positionTitle?: React.ReactNode;
        positionIndex?: number;
        pageIndex?: number;
        chapter?: string;
        onFocus?: () => void;
        [key: string]: any;
    };
    customComplete?: () => React.ReactNode;
    customAfterForm?: (itemIndex: number) => React.ReactNode;
    customControlsForLastRow?: (itemIndex: number) => React.ReactNode;
    customRowControls?: (itemIndex: number) => React.ReactNode;
    customSeparatorControls?: (setIndex: number) => React.ReactNode;
    hideViewModeSwitcher?: boolean;
    hideAddButton?: boolean;
    viewMode?: string;
    enableDragDrop?: boolean;
    draggedSetIndex?: number | null;
    onDragMove?: (setIndex: number | null) => void;
    onDragDrop?: (item: { setIndex: number }) => void;
    onAddAlternativePosition?: () => void;
    positionFields?: FormField[][];
    onPositionFieldUpdate?: (fieldName: string, update: any) => Promise<any> | void;
    generalConfig?: GeneralConfig;
}

const ItemDataSection = React.memo(
    ({
        itemType,
        formFieldGroups,
        additionalScrollOffset = 0,
        customTitle,
        customComplete,
        customAfterForm,
        customControlsForLastRow,
        customRowControls,
        customSeparatorControls,
        hideViewModeSwitcher = false,
        hideAddButton = false,
        viewMode: controlledViewMode,
        enableDragDrop = false,
        draggedSetIndex = null,
        onDragMove,
        onDragDrop,
        onAddAlternativePosition,
        positionFields,
        onPositionFieldUpdate,
        generalConfig,
    }: ItemDataSectionProps) => {
        const { t } = useTranslation('assistance');
        const {
            loading: isDocumentLoading,
            readOnly: isDocumentReadOnly,
            documentConfiguration,
        } = useAssistanceContext();

        const { document } = useAssistanceContext();
        const { isFastTrackEnabled, formsRef, itemVirtualizerRef } = useAssistanceViewContext();
        const tableScrollRef = useRef<HTMLDivElement>(null);
        const [controlsWidth, setControlsWidth] = useState<number | null>(null);

        const [filteredFormFieldGroups, { low: lowConfidenceFieldsCount, medium: mediumConfidenceFieldsCount }] =
            filterFastTrack(formFieldGroups);

        const [internalViewMode, setInternalViewMode] = useLocalStorage('assistanceItemsViewMode', 'table');

        // Use controlled viewMode if provided, otherwise use internal state
        const viewMode = controlledViewMode || internalViewMode;
        const setViewMode = hideViewModeSwitcher ? () => {} : setInternalViewMode;
        const [assistanceFormHeight, setAssistanceFormHeight] = useState(0);
        const [isOpen, setIsOpen] = useState(true);

        // resize columns
        const [columnWidths, setColumnWidths] = useState(null);
        const handleColumnWidthsChange = (widths) => {
            setColumnWidths(widths);
        };

        useEffect(() => {
            /*
        In table view the table should have a height so that the horizontal scrollbar is always visible.
        */

            if (!formsRef?.current) return;

            const handleResize = () => {
                const newHeight = formsRef?.current?.clientHeight;
                setAssistanceFormHeight(newHeight || 0);
            };

            const resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(formsRef?.current);

            handleResize();

            return () => resizeObserver.disconnect();
        }, []);

        const displayFormFieldGroups = isFastTrackEnabled ? filteredFormFieldGroups : formFieldGroups;

        // Measure the widest field-row controls cell so the TableHeader's first column
        // matches it and the header columns align with the field inputs below.
        useEffect(() => {
            if (viewMode !== 'table' || !tableScrollRef.current) return;
            const measure = () => {
                const formGroups = tableScrollRef.current?.querySelectorAll('.js-form-item-group');
                if (!formGroups || formGroups.length === 0) return;
                let maxWidth = 0;
                formGroups.forEach((fg) => {
                    const controls = fg.firstElementChild as HTMLElement | null;
                    if (controls) {
                        const w = controls.getBoundingClientRect().width;
                        if (w > maxWidth) maxWidth = w;
                    }
                });
                if (maxWidth > 0) setControlsWidth(maxWidth);
            };
            measure();
            const ro = new ResizeObserver(measure);
            ro.observe(tableScrollRef.current);
            return () => ro.disconnect();
        }, [viewMode, displayFormFieldGroups]);

        const initialColumnWidths = useMemo(() => {
            if (!displayFormFieldGroups?.length) return undefined;
            const firstFieldGroup = displayFormFieldGroups[0];
            return firstFieldGroup.map((field) => {
                if (field.fieldName.includes('description')) {
                    return 250;
                }
                if (field.fieldName.includes('number')) {
                    return 200;
                }
                return 100;
            });
        }, [displayFormFieldGroups]);

        const shouldVirtualize = displayFormFieldGroups?.length > VIRTUALIZATION_THRESHOLD;
        // Render all items during drag to keep DnD working for off-screen rows
        const virtualOverscan = draggedSetIndex !== null ? Infinity : 5;

        const renderItemContent = useCallback(
            (itemIndex: number) => {
                const fields = displayFormFieldGroups[itemIndex];
                if (!fields) return null;

                const currentSetIndex = fields[0]?.data?.updatePayload?.setIndex;
                const previousSetIndex =
                    itemIndex > 0
                        ? displayFormFieldGroups[itemIndex - 1]?.[0]?.data?.updatePayload?.setIndex
                        : undefined;
                const showSeparator =
                    enableDragDrop &&
                    itemIndex > 0 &&
                    currentSetIndex !== undefined &&
                    previousSetIndex !== undefined &&
                    currentSetIndex !== previousSetIndex;

                const isFirstInSet =
                    itemIndex === 0 ||
                    (enableDragDrop &&
                        currentSetIndex !== undefined &&
                        previousSetIndex !== undefined &&
                        currentSetIndex !== previousSetIndex);

                const showDropPreview = enableDragDrop && draggedSetIndex === currentSetIndex && showSeparator;

                return (
                    <React.Fragment>
                        {showSeparator && (
                            <>
                                {showDropPreview && <div className="bg-surface-strong rounded-lg h-8 w-full my-3" />}
                                {!showDropPreview && (
                                    <div className="flex flex-col justify-between w-full py-2">
                                        <div className="flex items-center flex-1">
                                            <div className="flex-1 h-px bg-line-weak" />
                                            <div className="flex items-center gap-1 px-2 py-1 rounded-sm border-solid border-line-weak border bg-surface-medium">
                                                <span className="flex-1 text-xs font-medium text-content-default">
                                                    {t('listOfServices.articleSetCard.or')}
                                                </span>
                                            </div>
                                            <div className="flex-1 h-px bg-line-weak" />
                                        </div>
                                        <div className="self-end">
                                            {customSeparatorControls && customSeparatorControls(currentSetIndex)}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        <ItemDataFormFieldGroup
                            itemType={itemType}
                            hideCrosshair={!!customTitle}
                            itemIndex={fields[0]?.itemIndex}
                            displayItemIndex={fields[0]?.displayItemIndex}
                            fields={fields}
                            columnWidths={columnWidths}
                            controlsWidth={controlsWidth}
                            additionalScrollOffset={additionalScrollOffset}
                            enableDragDrop={enableDragDrop}
                            isFirstInSet={isFirstInSet}
                            setIndex={currentSetIndex}
                            onDragMove={onDragMove}
                            onDragDrop={onDragDrop}
                            customRowControls={customRowControls}
                            customControls={customControlsForLastRow ? customControlsForLastRow(itemIndex) : null}
                        />
                        {customAfterForm && customAfterForm(itemIndex)}
                    </React.Fragment>
                );
            },
            [
                displayFormFieldGroups,
                enableDragDrop,
                draggedSetIndex,
                itemType,
                customTitle,
                columnWidths,
                additionalScrollOffset,
                onDragMove,
                onDragDrop,
                customRowControls,
                customControlsForLastRow,
                customAfterForm,
                customSeparatorControls,
                t,
            ]
        );

        const customTitleComponent = customTitle ? (
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <FocusButton onClick={() => customTitle.onFocus?.()} />
                <h4 className="font-medium text-sm truncate">{customTitle.positionNumber}</h4>
                {positionFields?.length > 0 && (
                    <div className="flex items-center border-l border-line-weak gap-2 pl-3 ml-auto mr-3">
                        {positionFields.map((fieldGroup) => {
                            const formField = fieldGroup[0];
                            return (
                                <div key={formField.fieldName} className="flex items-center gap-2">
                                    <label className="text-xs text-content-medium whitespace-nowrap">
                                        {t(`listOfServices.positionCard.${camelCase(formField.fieldName)}`)}
                                    </label>
                                    <div
                                        className={
                                            POSITION_FIELD_WIDTHS[formField.fieldName] ?? DEFAULT_POSITION_FIELD_WIDTH
                                        }
                                    >
                                        <AssistanceField
                                            fieldName={formField.fieldName}
                                            valueKey={formField.valueKey}
                                            value={formField.value}
                                            data={formField.data}
                                            config={formField.config}
                                            generalConfig={generalConfig}
                                            onUpdate={(payload) =>
                                                onPositionFieldUpdate?.(formField.fieldName, payload)
                                            }
                                            documentReadOnly={isDocumentReadOnly}
                                            documentLoading={isDocumentLoading}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        ) : null;

        return (
            <div data-item-type={itemType}>
                <AssistanceSection
                    title={customTitle ? customTitleComponent : t(`sections.${camelCase(itemType)}`)}
                    hideChevron={!!customTitle}
                    // let the custom title span the row so its position fields can right-align next to the controls
                    headerButtonClassnames={customTitle ? 'flex-1 min-w-0' : ''}
                    assistanceContentClassnames={customTitle && 'px-6 pt-2 pb-0'}
                    controls={
                        <ItemDataSectionControls
                            isOpen={isOpen}
                            mediumConfidenceFieldsCount={mediumConfidenceFieldsCount}
                            lowConfidenceFieldsCount={lowConfidenceFieldsCount}
                            viewMode={viewMode}
                            onViewModeChange={setViewMode}
                            onOpenChange={setIsOpen}
                            itemType={itemType}
                            hideViewModeSwitcher={hideViewModeSwitcher}
                            hideAddButton={hideAddButton}
                            customTitle={customTitle}
                            customComplete={customComplete}
                            onAddAlternativePosition={onAddAlternativePosition}
                        />
                    }
                    open={isOpen}
                    onOpenChange={setIsOpen}
                    viewMode={viewMode}
                    enableDragDrop={enableDragDrop}
                >
                    <DndProvider backend={HTML5Backend}>
                        <div className="w-full">
                            <div
                                ref={viewMode !== 'list' ? tableScrollRef : undefined}
                                className={classnames(
                                    'relative flex flex-col',
                                    viewMode === 'list' ? 'gap-8' : 'overflow-auto'
                                )}
                                style={
                                    viewMode !== 'list' && !customTitle
                                        ? { maxHeight: `calc(${assistanceFormHeight}px - 108px)` }
                                        : undefined
                                }
                            >
                                <div
                                    className={classnames(
                                        'relative flex flex-col',
                                        viewMode === 'list' ? 'gap-2' : 'gap-2 pb-2'
                                    )}
                                >
                                    {!document ? (
                                        <ItemDataSectionLoader />
                                    ) : displayFormFieldGroups?.length > 0 ? (
                                        <>
                                            {viewMode === 'table' && (
                                                <TableHeader
                                                    enableDragDrop={enableDragDrop}
                                                    hideFirstColumn={!!customTitle}
                                                    fieldGroups={displayFormFieldGroups}
                                                    onColumnWidthChange={handleColumnWidthsChange}
                                                    initialWidths={initialColumnWidths}
                                                    documentConfiguration={documentConfiguration}
                                                    firstColWidth={controlsWidth}
                                                />
                                            )}

                                            {enableDragDrop && draggedSetIndex === 0 && (
                                                <div className="bg-surface-strong rounded-lg h-8 w-full my-3" />
                                            )}

                                            {shouldVirtualize ? (
                                                <VirtualizedItemList
                                                    key={viewMode}
                                                    ref={itemVirtualizerRef}
                                                    count={displayFormFieldGroups.length}
                                                    scrollContainerRef={viewMode === 'list' ? formsRef : tableScrollRef}
                                                    renderItem={renderItemContent}
                                                    estimateSize={viewMode === 'list' ? 200 : 40}
                                                    overscan={virtualOverscan}
                                                    gap={8}
                                                />
                                            ) : (
                                                displayFormFieldGroups?.map((fields, itemIndex) => (
                                                    <React.Fragment key={fields[0]?.itemIndex}>
                                                        {renderItemContent(itemIndex)}
                                                    </React.Fragment>
                                                ))
                                            )}
                                        </>
                                    ) : (
                                        <AssistanceSectionEmptyState title="fastTrack.emptyState.description" />
                                    )}
                                </div>
                            </div>
                        </div>
                    </DndProvider>
                </AssistanceSection>
            </div>
        );
    }
);

const ItemDataSectionControls = ({
    isOpen,
    mediumConfidenceFieldsCount,
    lowConfidenceFieldsCount,
    viewMode,
    onViewModeChange,
    onOpenChange,
    itemType,
    hideViewModeSwitcher = false,
    hideAddButton = false,
    customTitle,
    customComplete,
    onAddAlternativePosition,
}: any) => {
    const { t } = useTranslation('assistance');

    const {
        record,
        document,
        loading: isDocumentLoading,
        isFinished,
        readOnly,
        documentConfiguration,
        handlers: { showOriginalData },
    } = useAssistanceContext();
    const { onFieldAction, onFieldFocus, onFieldReselect, generalConfig } = useAssistanceViewContext();

    const addLineItemButtonRef = React.useRef(null);

    const currentTab = useCurrentTabName();
    const isSourceTab = currentTab === TABS_OPTIONS.Source;
    const isEmailDocumentTab = currentTab === TABS_OPTIONS.EmailDocument;

    // whether the "add line item" pseudo field is being focused / reselected / updated
    const { isAddLineItemActive, isAddLineItemUpdating } = useInteractionSelector((s) => ({
        isAddLineItemActive: [s.focusField, s.reselectField, s.updateField].some(
            (field) => field?.itemIndex === ADD_LINE_ITEM_INDEX
        ),
        isAddLineItemUpdating: s.updateField?.itemIndex === ADD_LINE_ITEM_INDEX,
    }));

    const showDrawHint = isAddLineItemActive && !isAddLineItemUpdating;

    useReselectEventListener(addLineItemButtonRef, (e: ReselectEvent) => {
        onFieldReselect({
            itemIndex: ADD_LINE_ITEM_INDEX,
            itemType: itemType,
            isEmailOcr: e.detail.isEmailOcr,
            bbox: e.detail.bbox,
            pageIndex: e.detail.pageIndex,
        });
    });

    if (!document) {
        return null;
    }

    const handleAddLineItem = () => {
        if (documentConfiguration?.onAddLineItem) {
            return documentConfiguration.onAddLineItem({
                onFieldAction,
                itemType,
                itemIndex: ADD_LINE_ITEM_INDEX,
            });
        }

        const { focusField, reselectField, updateField } = interactionStore.getState();
        return onFieldFocus(
            focusField?.itemIndex === ADD_LINE_ITEM_INDEX ||
                reselectField?.itemIndex === ADD_LINE_ITEM_INDEX ||
                updateField?.itemIndex === ADD_LINE_ITEM_INDEX
                ? null
                : {
                      itemIndex: ADD_LINE_ITEM_INDEX,
                      reselectTargetRef: addLineItemButtonRef,
                  }
        );
    };

    const isLineItemsCorrect =
        record?.[`is${titleCase(itemType)}Correct`] ??
        (record?.isLineItemsCorrect && JSON.parse(record.isLineItemsCorrect)?.[snakeCase(itemType)]);

    const canFinishLineItemsAssistance =
        record?.[`canFinish${titleCase(itemType)}Assistance`] ??
        (record?.canFinishLineItemsAssistance &&
            JSON.parse(record?.canFinishLineItemsAssistance)?.[snakeCase(itemType)]);

    const canComplete = !!canFinishLineItemsAssistance && !readOnly;

    const correctBadge = (
        <span className="text-sm font-medium text-content-strong flex gap-1.5 items-center px-2">
            <CompleteIcon className="text-content-success-strong" />
            {t('itemsView.actions.sectionCompleted')}
        </span>
    );

    return (
        <>
            {isOpen && !hideViewModeSwitcher && (
                <>
                    <div className="flex gap-1">
                        <Tooltip content={t('itemsView.actions.listView')} side="top">
                            <AssistanceSection.IconButton
                                active={viewMode === 'list'}
                                onClick={() => onViewModeChange('list')}
                            >
                                <ListViewIcon />
                            </AssistanceSection.IconButton>
                        </Tooltip>

                        <Tooltip content={t('itemsView.actions.tableView')} side="top">
                            <AssistanceSection.IconButton
                                active={viewMode === 'table'}
                                onClick={() => onViewModeChange('table')}
                            >
                                <TableViewIcon />
                            </AssistanceSection.IconButton>
                        </Tooltip>
                    </div>

                    <AssistanceSection.ButtonSeparator />
                </>
            )}

            {!readOnly && isOpen && !hideAddButton && (
                <>
                    <Tooltip
                        content={
                            showDrawHint ? t('itemsView.actions.addArticleDrawHint') : t('itemsView.actions.addArticle')
                        }
                        open={showDrawHint || undefined}
                        side="top"
                    >
                        <AssistanceSection.IconButton
                            active={isAddLineItemActive}
                            onClick={handleAddLineItem}
                            disabled={isDocumentLoading || readOnly || isSourceTab || isEmailDocumentTab}
                            ref={addLineItemButtonRef}
                        >
                            {isAddLineItemUpdating ? <LoadingIcon spin /> : <AddItemIcon />}
                        </AssistanceSection.IconButton>
                    </Tooltip>

                    <AssistanceSection.ButtonSeparator />
                </>
            )}

            {isOpen && !readOnly && generalConfig?.allowRequestStock && (
                <>
                    <Tooltip content={t('itemsView.actions.request_stock_all')} side="top">
                        <AssistanceSection.IconButton
                            onClick={() => onFieldAction?.({ action: 'action:request_stock_all' })}
                            disabled={isDocumentLoading}
                        >
                            {isDocumentLoading ? <LoadingIcon spin /> : <RequestStockIcon />}
                        </AssistanceSection.IconButton>
                    </Tooltip>

                    <AssistanceSection.ButtonSeparator />
                </>
            )}

            {/* line item rules only run for the default item type */}
            {isOpen && itemType === DEFAULT_LINE_ITEM_TYPE && <FineTuningPopover level="lineItem" />}

            {isLineItemsCorrect && !showOriginalData
                ? correctBadge
                : (!isOpen || showOriginalData) && (
                      <>
                          <div className="px-2 flex gap-2">
                              {mediumConfidenceFieldsCount === 0 && lowConfidenceFieldsCount === 0 && (
                                  <div className="rounded-full w-7 h-7 text-content-strong text-sm font-medium flex justify-center items-center bg-surface-strong">
                                      0
                                  </div>
                              )}
                              {mediumConfidenceFieldsCount > 0 && (
                                  <div className="rounded-full w-7 h-7 text-content-strong text-sm font-medium flex justify-center items-center bg-surface-warning-strong">
                                      {mediumConfidenceFieldsCount}
                                  </div>
                              )}
                              {lowConfidenceFieldsCount > 0 && (
                                  <div className="rounded-full w-7 h-7 text-content-white dark:text-content-black text-sm font-medium flex justify-center items-center bg-surface-danger-strong">
                                      {lowConfidenceFieldsCount}
                                  </div>
                              )}
                          </div>
                      </>
                  )}

            {!isFinished && !showOriginalData && isOpen && (
                <>
                    {customTitle && onAddAlternativePosition && (
                        <>
                            <Tooltip content={t('listOfServices.articleSetCard.addAlternative')} side="top">
                                <AssistanceSection.IconButton
                                    className="p-2"
                                    onClick={onAddAlternativePosition}
                                    disabled={isDocumentLoading || readOnly}
                                >
                                    <AddAlternativeIcon />
                                </AssistanceSection.IconButton>
                            </Tooltip>
                            <AssistanceSection.ButtonSeparator />
                        </>
                    )}
                    {customComplete ? (
                        customComplete()
                    ) : !customTitle ? (
                        <Tooltip
                            content={
                                !canComplete || isDocumentLoading
                                    ? t('itemsView.actions.completeSectionDisabled')
                                    : t('itemsView.actions.completeSection')
                            }
                            side="top"
                        >
                            <AssistanceSection.IconButton
                                data-testid={createTestId(ElementType.Button, UseCase.Orders, 'finishLineItems')}
                                className="bg-surface-brand-strong-default hover:bg-surface-brand-strong-hover active:bg-surface-brand-strong-active text-content-white dark:text-content-black hover:text-content-white dark:text-content-black disabled:opacity-40 disabled:pointer-events-none"
                                onClick={() => {
                                    onFieldAction?.({
                                        action: 'action:mark_items_correct',
                                        itemType: itemType,
                                    }).then(() => {
                                        onOpenChange(false);
                                    });
                                }}
                                disabled={!canComplete || isDocumentLoading}
                            >
                                {isDocumentLoading ? <LoadingIcon spin /> : <CompleteIcon />}
                            </AssistanceSection.IconButton>
                        </Tooltip>
                    ) : null}
                </>
            )}
        </>
    );
};

const ItemDataSectionLoader = () => (
    <>
        <AssistanceSection.FieldGroupLoadingSkeleton />
        <AssistanceSection.FieldGroupLoadingSkeleton />
        <AssistanceSection.FieldGroupLoadingSkeleton />
        <AssistanceSection.FieldGroupLoadingSkeleton />
    </>
);
const getFieldNameTranslation = (t, fieldName: string, documentConfiguration): string => {
    const documentTypeName = documentConfiguration?.documentTypeName;

    if (!documentTypeName) {
        return t(`assistance:itemsView.fieldNames.${fieldName}`);
    }

    const specificKey = `assistance:${documentTypeName}.articleSetCard.fieldNames.${fieldName}`;
    const specificTranslation = t(specificKey, { defaultValue: '' });

    if (specificTranslation) {
        return specificTranslation;
    }

    return t(`assistance:itemsView.fieldNames.${fieldName}`);
};

const TableHeader = ({
    fieldGroups,
    onColumnWidthChange,
    hideFirstColumn,
    enableDragDrop,
    initialWidths,
    documentConfiguration,
    firstColWidth: firstColWidthOverride,
}: any) => {
    const { t } = useTranslation('assistance');

    const firstFieldGroup = fieldGroups?.[0];
    const firstColWidthClass = hideFirstColumn
        ? enableDragDrop
            ? 'w-[2.25rem]'
            : 'w-0'
        : firstColWidthOverride
          ? ''
          : 'w-[4.15rem]';

    return (
        <div className="flex sticky top-0 z-20 flex-none bg-surface-weak mb-0.5">
            <div
                className={`${firstColWidthClass} flex-none bg-surface-weak sticky left-0 z-10`}
                style={!hideFirstColumn && firstColWidthOverride ? { width: firstColWidthOverride } : undefined}
            />

            <SplitPanes
                id={TABLE_HEADER_SPLIT_PANEL_ID}
                shrinkPanels={false}
                onUpdate={onColumnWidthChange}
                className="!w-auto bg-surface-weak py-1 gap-1"
                allowResizeLast={true}
                initialWidths={initialWidths}
            >
                {firstFieldGroup?.map((field) => (
                    <Pane
                        key={`items-table-header-${field.fieldName}`}
                        className="flex items-center !flex-row px-2 py-1 js-table-header-item box-border flex-none min-w-0 truncate"
                    >
                        <label className="text-xs text-content-medium flex-none">
                            <span>
                                {field.config?.displayName ??
                                    field.config?.fieldLabelName ??
                                    getFieldNameTranslation(t, field.fieldName, documentConfiguration)}{' '}
                                {field.config?.isRequired && <span className="text-content-danger-strong">*</span>}
                            </span>
                        </label>
                    </Pane>
                ))}
            </SplitPanes>
        </div>
    );
};

export const FocusActionClickButton = ({
    previewRef,
    viewMode,
    itemIndex,
    isButtonEnabled = true,
    additionalScrollOffset = 0,
}: any) => {
    const { t } = useTranslation('assistance');

    const handleFocusActionClick = () => {
        const triggers = previewRef?.current.querySelectorAll('.js-image-map-row');
        const trigger = triggers?.[itemIndex];
        if (!trigger) return;

        const triggerPos = trigger.getBoundingClientRect();
        const parentPos = previewRef?.current.getBoundingClientRect();

        previewRef?.current.scrollTo({
            top: triggerPos.top + previewRef?.current.scrollTop - parentPos.top - additionalScrollOffset,
            behavior: 'smooth',
            block: 'start',
        });
    };

    return (
        <>
            {isButtonEnabled && handleFocusActionClick && (
                <Tooltip content={t(`assistance:itemsView.actions.focus`)} side="top">
                    <AssistanceSection.FieldGroupIconButton
                        onClick={handleFocusActionClick}
                        className={classnames(viewMode !== 'list' && 'w-8 h-8 -mr-1')}
                    >
                        <FocusIcon />
                    </AssistanceSection.FieldGroupIconButton>
                </Tooltip>
            )}
        </>
    );
};

const PREFERRED_ITEM_LOOKUP_FIELD_NAME = 'article_number';

const ItemDataFormFieldGroup = ({
    itemType,
    fields,
    hideCrosshair,
    itemIndex,
    displayItemIndex,
    columnWidths,
    controlsWidth,
    additionalScrollOffset = 0,
    enableDragDrop = false,
    isFirstInSet = false,
    setIndex,
    onDragMove,
    onDragDrop,
    customRowControls,
    customControls,
}: any) => {
    const { t } = useTranslation('assistance');
    const { document, record, loading: documentLoading, readOnly, documentConfiguration } = useAssistanceContext();
    const [isMatchHovered, setIsMatchHovered] = useState(false);
    const [isMatchPinned, setIsMatchPinned] = useState(false);
    const showMatch = isMatchHovered || isMatchPinned;
    const togglePin = useCallback(() => setIsMatchPinned((pinned) => !pinned), []);

    const {
        generalConfig,
        onFieldAction,
        onFieldFocus,
        onFieldBlur,
        onFieldMouseEnter,
        onFieldMouseLeave,
        onFieldReselect,
        onFieldUpdate,
        previewRef,
    } = useAssistanceViewContext();
    const { viewMode } = useAssistanceSectionContext();

    // this is probably not the best way compared to using a AssistanceItemContext or similar
    const item = document?.[camelCase(itemType)]?.[itemIndex] || {};

    const lookupFields = fields?.filter((field) => field.config?.performLookups) || [];
    const lookupField =
        lookupFields?.find((field) => field.fieldName === PREFERRED_ITEM_LOOKUP_FIELD_NAME) || lookupFields?.[0];
    const fieldTypeName = lookupField?.data?.__typename;

    const mapping = useMapping(fieldTypeName, lookupField?.fieldName, lookupField?.value);
    const handleMappingChange = useUpdateMapping(fieldTypeName, lookupField?.fieldName, mapping?.id);

    const handleActionClick = (action: string, payload = undefined) =>
        onFieldAction({ itemIndex, itemType, action: `action:${action}`, payload });

    const FieldGroupComponent = useConditionalComponent(
        'ItemDataFieldGroup',
        {
            itemIndex,
            fields,
            documentTypeName: documentConfiguration?.documentTypeName,
        },
        ItemDataFieldGroup
    );

    const FocusActionClickComponent = useConditionalComponent(
        'FocusActionClickButton',
        {
            itemIndex,
            fields,
        },
        FocusActionClickButton
    );

    const MoreActionsButtonComponent = useConditionalComponent(
        'ItemMoreActionsButton',
        {
            itemIndex,
            fields,
        },
        ItemMoreActionsButton
    );

    // Drag and drop logic for article sets (only for LV)
    const dragRef = useRef(null);
    const dragPreviewRef = useRef(null);
    const dropRef = useRef(null);

    const [, drop] = useDrop({
        accept: DRAGGABLE_ARTICLE_SET_TYPE,
        hover(item: any, monitor) {
            if (!enableDragDrop || !isFirstInSet || !dropRef.current) return;

            const dragIndex = item.setIndex;
            const hoverIndex = setIndex;

            if (dragIndex === hoverIndex) {
                onDragMove?.(null);
                return;
            }

            const hoverBoundingRect = dropRef.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (hoverClientY < hoverMiddleY) {
                onDragMove?.(hoverIndex);
                return;
            } else {
                onDragMove?.(hoverIndex + 1);
                return;
            }
        },
        canDrop: () => enableDragDrop,
    });

    const [{ isDragging }, drag, dragPreview] = useDrag({
        type: DRAGGABLE_ARTICLE_SET_TYPE,
        item: () => ({ setIndex }),
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
            if (enableDragDrop) {
                onDragDrop?.(item);
            }
        },
        canDrag: () => enableDragDrop && isFirstInSet,
    });

    if (enableDragDrop && isFirstInSet) {
        drag(dragRef);
        drop(dragPreview(dragPreviewRef));
        drop(dropRef);
    }

    const canPreviewMatch = record?.isMatchingEnabled && !item?.isMatchingIgnored && Boolean(item?.matchedLineItem);
    const matchIndicator = canPreviewMatch ? (
        <LineItemMatchIndicator
            isActive={showMatch}
            isPinned={isMatchPinned}
            sourceDocumentNumber={document?.orderNumber?.value}
            onHoverChange={setIsMatchHovered}
            onClick={togglePin}
        />
    ) : null;

    const prevItem = document?.[camelCase(itemType)]?.[itemIndex - 1];
    const nextItem = document?.[camelCase(itemType)]?.[itemIndex + 1];
    const isPrevItemSubLineItem = Boolean(prevItem?.parentLineItemId);
    const isNextItemSubLineItem = Boolean(nextItem?.parentLineItemId);
    const isSubLineItem = Boolean(item?.parentLineItemId);

    return (
        <>
            <div
                className={classnames(
                    'relative isolate',
                    isSubLineItem && 'bg-surface-medium',
                    viewMode === 'list' && isSubLineItem && '-ml-6 pl-6 -mr-6 pr-6 py-2',
                    viewMode === 'list' &&
                        isSubLineItem &&
                        !isPrevItemSubLineItem &&
                        'mt-2 pt-4 border-t border-line-weak',
                    viewMode === 'list' && isSubLineItem && isPrevItemSubLineItem && '-mt-2',
                    viewMode === 'list' &&
                        isSubLineItem &&
                        !isNextItemSubLineItem &&
                        'mb-2 pb-4 border-b border-line-weak'
                )}
            >
                {viewMode === 'list' && canPreviewMatch && showMatch && (
                    <div className="absolute top-0 inset-x-0 h-8 pointer-events-none flex items-center gap-1 -z-10">
                        <div className="w-36 flex-none" />
                        <div className="flex items-stretch w-full min-w-0">
                            <div className="flex-1 min-w-0">
                                <div className="pr-2 truncate text-sm font-medium text-content-strong">
                                    {t('itemsView.matchedLineItem.documentValues')}
                                </div>
                            </div>
                            <div className="flex-1 min-w-0 pl-3">
                                <div className="pr-2 truncate text-sm font-medium text-content-strong">
                                    {t('itemsView.matchedLineItem.fromOrder')}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <FieldGroupComponent
                    itemIndex={itemIndex}
                    setIndex={setIndex}
                    title={
                        // Hide title in list view for LV articles (identified by hidden crosshair being passed from parent)
                        viewMode === 'list' && hideCrosshair ? null : (
                            <>
                                {isSubLineItem && <SubLineItemIcon className="mr-2" />}
                                {`${t('itemsView.groupTitle')} #${getItemDisplayIndex(itemIndex, displayItemIndex)}`}
                                {item?.isMatchingIgnored && (
                                    <span className="text-content-medium ml-3 inline-flex gap-1 items-baseline text-xs">
                                        <WarningIcon className="translate-y-px text-content-weak" />
                                        <span>{t('itemsView.matchingIgnored')}</span>
                                    </span>
                                )}
                                {matchIndicator}
                            </>
                        )
                    }
                    fields={fields}
                    wrapperRef={enableDragDrop && isFirstInSet ? dropRef : undefined}
                    isDragging={enableDragDrop ? isDragging : false}
                    controls={
                        <>
                            {viewMode !== 'list' && matchIndicator}

                            {mapping && viewMode === 'list' && (
                                <MappingDropdownMenu mapping={mapping} onChange={handleMappingChange} />
                            )}

                            {!hideCrosshair && (
                                <FocusActionClickComponent
                                    previewRef={previewRef}
                                    viewMode={viewMode}
                                    itemIndex={itemIndex}
                                    additionalScrollOffset={additionalScrollOffset}
                                />
                            )}

                            {/* Only show drag icon in table view controls, list view shows it inline */}
                            {enableDragDrop && viewMode !== 'list' ? (
                                isFirstInSet && !readOnly ? (
                                    <div
                                        ref={dragRef}
                                        className="flex items-center justify-center rounded transition-colors duration-200 h-8 hover:bg-surface-strong cursor-move px-2"
                                        style={{ opacity: isDragging ? 0.5 : 1 }}
                                    >
                                        <DragIcon />
                                    </div>
                                ) : (
                                    !isFirstInSet && (
                                        <div className="flex items-center justify-center rounded transition-colors duration-200 h-8 px-3" />
                                    )
                                )
                            ) : null}

                            {!readOnly && !hideCrosshair ? (
                                <MoreActionsButtonComponent
                                    item={item}
                                    onActionClick={handleActionClick}
                                    itemIndex={itemIndex}
                                    displayItemIndex={displayItemIndex}
                                    itemType={itemType}
                                    fieldNames={fields.map((formField) => formField.fieldName)}
                                    disabled={readOnly}
                                    hideCrosshair={hideCrosshair}
                                />
                            ) : viewMode !== 'list' && !hideCrosshair ? (
                                mapping ? (
                                    <MappingDropdownMenu
                                        align="start"
                                        mapping={mapping}
                                        onChange={handleMappingChange}
                                        hideValue
                                    />
                                ) : (
                                    <span className="flex w-8 h-8 items-center justify-center text-sm font-medium ">
                                        {getItemDisplayIndex(itemIndex, displayItemIndex)}
                                    </span>
                                )
                            ) : null}
                        </>
                    }
                >
                    {fields.map((field, fieldIndex) => {
                        const fullFieldName =
                            field.valueKey && field.valueKey !== DEFAULT_VALUE_KEY
                                ? `${field.fieldName}${GROUP_SEPARATOR}${field.valueKey}`
                                : field.fieldName;

                        const isDraggable = enableDragDrop && !readOnly;
                        const isListView = viewMode === 'list';

                        const showInline = isListView && showMatch && Boolean(item?.matchedLineItem);

                        return (
                            <React.Fragment key={field.path}>
                                <div
                                    className={classnames({
                                        'flex flex-row': isDraggable && fieldIndex === 0 && isFirstInSet,
                                        'pl-8': isDraggable && isListView && (fieldIndex > 0 || !isFirstInSet),
                                    })}
                                >
                                    {viewMode === 'list' &&
                                        enableDragDrop &&
                                        fieldIndex === 0 &&
                                        isFirstInSet &&
                                        !readOnly && (
                                            <div
                                                ref={dragRef}
                                                className="flex items-center justify-center rounded transition-colors duration-200 h-8 hover:bg-surface-strong cursor-move px-2 mr-2"
                                                style={{ opacity: isDragging ? 0.5 : 1 }}
                                            >
                                                <DragIcon />
                                            </div>
                                        )}
                                    <AssistanceSection.FieldLabel
                                        label={
                                            field.config?.displayName ??
                                            field.config?.fieldLabelName ??
                                            getFieldNameTranslation(t, field.fieldName, documentConfiguration)
                                        }
                                        required={field.config?.isRequired}
                                        style={viewMode !== 'list' ? { width: columnWidths?.[fieldIndex] } : undefined}
                                        className={classnames(
                                            viewMode === 'list' &&
                                                enableDragDrop &&
                                                fieldIndex === 0 &&
                                                isFirstInSet &&
                                                !readOnly &&
                                                'flex-1'
                                        )}
                                    >
                                        <div className="flex items-stretch w-full min-w-0">
                                            <div className="min-w-0 flex-1 transition-all duration-300 ease-out">
                                                <MemoAssistanceField
                                                    fieldName={field.fieldName}
                                                    valueKey={field.valueKey}
                                                    value={field.value}
                                                    fieldIndex={0}
                                                    itemIndex={itemIndex}
                                                    itemType={itemType}
                                                    placeholder={
                                                        field.config?.displayName ??
                                                        field.config?.fieldLabelName ??
                                                        getFieldNameTranslation(t, fullFieldName, documentConfiguration)
                                                    }
                                                    data={field.data}
                                                    config={field.config}
                                                    generalConfig={generalConfig}
                                                    onFocus={onFieldFocus}
                                                    onBlur={onFieldBlur}
                                                    onMouseEnter={onFieldMouseEnter}
                                                    onMouseLeave={onFieldMouseLeave}
                                                    onAction={onFieldAction}
                                                    onReselect={onFieldReselect}
                                                    onUpdate={onFieldUpdate}
                                                    viewMode={viewMode}
                                                    documentLoading={documentLoading}
                                                    documentReadOnly={readOnly}
                                                    clientPartitionId={
                                                        document?.[camelCase(document.clientPartitionFieldName)]
                                                            ?.customerNumber
                                                    }
                                                    matchingDocumentNumber={
                                                        document[camelCase(field.config?.documentNumberFieldName || '')]
                                                            ?.value
                                                    }
                                                />
                                            </div>
                                            {isListView && item?.matchedLineItem && (
                                                <InlineMatchedValue
                                                    item={item}
                                                    fieldName={field.fieldName}
                                                    isVisible={showInline}
                                                />
                                            )}
                                        </div>
                                    </AssistanceSection.FieldLabel>
                                </div>
                            </React.Fragment>
                        );
                    })}
                    {(customControls || (customRowControls && customRowControls(itemIndex))) && (
                        <div className="flex justify-end sticky right-0 bg-surface-white">
                            {customRowControls && customRowControls(itemIndex)}

                            {customControls}
                        </div>
                    )}
                </FieldGroupComponent>
                {viewMode !== 'list' && canPreviewMatch && (
                    <MatchedFieldsRow
                        item={item}
                        fields={fields}
                        columnWidths={columnWidths}
                        controlsWidth={controlsWidth}
                        isVisible={showMatch}
                    />
                )}
            </div>
        </>
    );
};

type DeleteRangeAction = 'delete_and_below' | 'delete_and_above';

const isPosition = (item: any): boolean => !item?.parentLineItemId;

export const ItemMoreActionsButton = ({
    item,
    onActionClick,
    itemIndex,
    itemType = 'line_items',
    displayItemIndex,
    fieldNames,
    children,
    className,
    hideCrosshair,
    ...props
}: any) => {
    const { t } = useTranslation('assistance');

    const { record, document } = useAssistanceContext();
    const { generalConfig } = useAssistanceViewContext();

    const { viewMode } = useAssistanceSectionContext();

    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deleteRangeAction, setDeleteRangeAction] = useState<DeleteRangeAction | null>(null);
    const [ignoreMatchingModalVisible, setIgnoreMatchingModalVisible] = useState(false);

    const handleConfirmIgnoreMatching = () => {
        onActionClick('document_matching_ignore_matching');
        setIgnoreMatchingModalVisible(false);
    };

    const handleConfirmDeleteRange = () => {
        onActionClick(deleteRangeAction);
        setDeleteRangeAction(null);
    };

    const isSubLineItem = !isPosition(item);

    const items: any[] = document?.[camelCase(itemType)] ?? [];
    const showDeleteAndAbove = !isSubLineItem && items.findIndex(isPosition) < itemIndex;
    const showDeleteAndBelow = !isSubLineItem && items.findLastIndex(isPosition) > itemIndex;

    return (
        <>
            <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                    <AssistanceSection.FieldGroupIconButton
                        onClick={() => ''}
                        className={classnames('group', viewMode !== 'list' && 'w-8 h-8', className)}
                        {...props}
                    >
                        {viewMode !== 'list' && !hideCrosshair && (
                            <span className="block group-data-[state=open]:hidden group-hover:hidden">
                                {isSubLineItem ? <SubLineItemIcon /> : getItemDisplayIndex(itemIndex, displayItemIndex)}
                            </span>
                        )}
                        <span
                            className={classnames(
                                'group-data-[state=open]:block',
                                viewMode !== 'list' && !hideCrosshair && 'hidden  group-hover:block'
                            )}
                        >
                            <MoreIcon />
                        </span>
                    </AssistanceSection.FieldGroupIconButton>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content align="end">
                    {children}

                    {!props?.isCorrectActionDisabled && (
                        <DropdownMenu.Item onClick={() => onActionClick('correct')}>
                            {t('itemsView.actions.correct')}
                        </DropdownMenu.Item>
                    )}

                    <DropdownMenu.Item onClick={() => onActionClick('duplicate', { itemIndex })}>
                        {t('itemsView.actions.duplicate')}
                    </DropdownMenu.Item>

                    {generalConfig?.allowRequestStock && (
                        <DropdownMenu.Item onClick={() => onActionClick('request_stock')}>
                            {t('itemsView.actions.request_stock')}
                        </DropdownMenu.Item>
                    )}

                    {item?.id &&
                        (isSubLineItem ? (
                            <>
                                <DropdownMenu.Item
                                    onClick={() =>
                                        onActionClick('add_sub_line_item', {
                                            parentLineItemId: item.parentLineItemId,
                                            beforeLineItemId: item.id,
                                        })
                                    }
                                >
                                    {t('itemsView.actions.add_sub_line_item_before')}
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                    onClick={() =>
                                        onActionClick('add_sub_line_item', {
                                            parentLineItemId: item.parentLineItemId,
                                            afterLineItemId: item.id,
                                        })
                                    }
                                >
                                    {t('itemsView.actions.add_sub_line_item_after')}
                                </DropdownMenu.Item>
                            </>
                        ) : (
                            <DropdownMenu.Item
                                onClick={() => onActionClick('add_sub_line_item', { parentLineItemId: item.id })}
                            >
                                {t('itemsView.actions.add_sub_line_item')}
                            </DropdownMenu.Item>
                        ))}

                    {fieldNames
                        ?.filter((fieldName) => !fieldName.startsWith('articleNumber'))
                        .map((fieldName) => {
                            // action:copy does not make sense for article numbers due to the dependencies to allowed units, allowed variants, etc.
                            const label = t(`itemsView.fieldNames.${snakeCase(fieldName)}`);
                            return (
                                <DropdownMenu.Item key={fieldName} onClick={() => onActionClick('copy', { fieldName })}>
                                    {t('itemsView.actions.copy', { fieldName: label })}
                                </DropdownMenu.Item>
                            );
                        })}
                    {fieldNames && fieldNames.length > 0 ? <DropdownMenu.Separator /> : null}

                    {record?.isMatchingEnabled && !item?.isMatchingIgnored && (
                        <>
                            <DropdownMenu.Item
                                className="menu-item--danger"
                                onClick={() => setIgnoreMatchingModalVisible(true)}
                            >
                                {t('itemsView.actions.document_matching_ignore_matching')}
                            </DropdownMenu.Item>
                            <DropdownMenu.Separator />
                        </>
                    )}

                    {record?.isMatchingEnabled && item?.isMatchingIgnored && (
                        <>
                            <DropdownMenu.Item
                                className="menu-item--danger"
                                onClick={() => onActionClick('document_matching_restore_matching')}
                            >
                                {t('itemsView.actions.document_matching_restore_matching')}
                            </DropdownMenu.Item>
                            <DropdownMenu.Separator />
                        </>
                    )}

                    <DropdownMenu.Item className="menu-item--danger" onClick={() => onActionClick('delete')}>
                        {t('itemsView.actions.delete')}
                    </DropdownMenu.Item>
                    {showDeleteAndAbove && (
                        <DropdownMenu.Item
                            className="menu-item--danger"
                            onClick={() => setDeleteRangeAction('delete_and_above')}
                        >
                            {t('itemsView.actions.delete_and_above')}
                        </DropdownMenu.Item>
                    )}
                    {showDeleteAndBelow && (
                        <DropdownMenu.Item
                            className="menu-item--danger"
                            onClick={() => setDeleteRangeAction('delete_and_below')}
                        >
                            {t('itemsView.actions.delete_and_below')}
                        </DropdownMenu.Item>
                    )}
                    <DropdownMenu.Item className="menu-item--danger" onClick={() => setDeleteModalVisible(true)}>
                        {t('itemsView.actions.bulk_delete')}
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu>

            <ConfirmModal
                title={t('itemsView.deleteModal.title')}
                labelConfirm={t('itemsView.deleteModal.confirmButton')}
                labelCancel={t('itemsView.deleteModal.cancelButton')}
                onConfirm={() => onActionClick('bulk_delete')} // Bulk delete
                onCancel={() => setDeleteModalVisible(false)}
                visible={deleteModalVisible}
                danger
            >
                <p>{t('itemsView.deleteModal.message')}</p>
            </ConfirmModal>

            <ConfirmModal
                title={deleteRangeAction && t(`itemsView.actions.${deleteRangeAction}`)}
                labelConfirm={t('itemsView.deleteRangeModal.confirmButton')}
                labelCancel={t('itemsView.deleteRangeModal.cancelButton')}
                onConfirm={handleConfirmDeleteRange}
                onCancel={() => setDeleteRangeAction(null)}
                visible={deleteRangeAction !== null}
                danger
            >
                <p>
                    {deleteRangeAction &&
                        t(`itemsView.deleteRangeModal.${deleteRangeAction}`, {
                            item: getItemDisplayIndex(itemIndex, displayItemIndex),
                        })}
                </p>
            </ConfirmModal>

            <ConfirmModal
                title={t('itemsView.ignoreMatchingModal.title')}
                labelConfirm={t('itemsView.ignoreMatchingModal.confirmButton')}
                labelCancel={t('itemsView.ignoreMatchingModal.cancelButton')}
                onConfirm={handleConfirmIgnoreMatching}
                onCancel={() => setIgnoreMatchingModalVisible(false)}
                visible={ignoreMatchingModalVisible}
                danger
            >
                <p>{t('itemsView.ignoreMatchingModal.message')}</p>
            </ConfirmModal>
        </>
    );
};

export default ItemDataSection;
